// The response: a view toggle, and the empty and error states. See Design >
// Exchange, "Consumer API".

import { EmptyState, Notice, PillGroup } from "../../../../ui/src/index.ts";
import type { PillOption } from "../../../../ui/src/index.ts";
import { useState } from "preact/hooks";
import { describeFailure } from "../../api.ts";
import { usd } from "../../domain/money.ts";
import type { AskRequest, QuestionResult } from "../../domain/types.ts";
import { readRequest } from "../actions.ts";
import { setState, useStore, type ResponseView } from "../store.ts";
import { AnswerView, FlowView, HubTable, JsonView } from "./views.tsx";

// One body per view; a new view fails to compile until it has one.
const ResponseBody = ({ view, q, request, value }: { view: ResponseView; q: QuestionResult; request: AskRequest | null; value: unknown }) => {
  switch (view) {
    case "answer": return <AnswerView q={q} request={request} />;
    case "table": return <HubTable q={q} />;
    case "flow": return <FlowView q={q} />;
    case "json": return <JsonView value={value} />;
    default: return view satisfies never;
  }
};

const VIEWS: PillOption<ResponseView>[] = [{ value: "answer", label: "Answer" }, { value: "table", label: "Table" }, { value: "flow", label: "Answer flow" }, { value: "json", label: "JSON" }];

export function ResponsePanel() {
  const s = useStore((x) => x);
  const [qi, setQi] = useState(0);
  const { request } = readRequest(s);
  const res = s.response;
  const q = res?.kind === "ok" ? res.value.questions[Math.min(qi, res.value.questions.length - 1)] : undefined;
  const status = s.sending ? "sending" : !res ? "not sent yet" : res.kind === "error" ? "failed" : s.stale ? "ran earlier" : `ran just now · charged ${usd(res.value.totalCharged)}`;

  return (
    <section class="cs-response" aria-label="Response">
      <div class="cs-response__head">
        <div class="cs-response__title"><h2>Response</h2> <span class="cs-muted">· {status}</span>
          {q?.selection && !s.stale && <span class="cs-timing"> · found the hubs in {q.selection.ms} ms, answered in {q.ms} ms</span>}</div>
        <PillGroup label="Response view" options={VIEWS} value={s.view} onChange={(v) => setState({ view: v })} />
      </div>
      {res?.kind === "ok" && res.value.questions.length > 1 && (
        <div class="ui-pills cs-qtabs" role="tablist" aria-label="Questions">
          {res.value.questions.map((x, i) => <button type="button" role="tab" class="ui-pill" key={x.id} aria-selected={i === qi} onClick={() => setQi(i)}>{x.id}</button>)}
        </div>
      )}
      <div class="cs-response__body" aria-busy={s.sending}>
        {s.stale && res?.kind === "ok" && <Notice tone="neutral" title="Response from your previous send. The request has changed since." />}
        <div class={s.stale ? "cs-stale" : ""}>
          {s.sending && <EmptyState>Sending the request…</EmptyState>}
          {!s.sending && !res && <EmptyState>Send the request to see each hub's response.</EmptyState>}
          {!s.sending && res?.kind === "error" && (
            <Notice tone="danger" title="That didn't work.">
              <p class="cs-flat">{describeFailure(res.error)}</p><span class="mono cs-muted">{res.error._tag}{res.error.status ? ` · ${res.error.status}` : ""}</span>
            </Notice>
          )}
          {!s.sending && q && res?.kind === "ok" && (
            <ResponseBody view={s.view} q={q} request={request} value={res.value} />
          )}
        </div>
      </div>
    </section>
  );
}
